

import { useState, useEffect } from "react";
import { Task, TaskStatus, Priority, useTaskStore } from "@/stores/taskStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

interface TaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: Task | null;
  projectId: string | null;
  defaultStatus?: TaskStatus;
  onSuccess: () => void;
}

const priorities: { value: Priority; label: string }[] = [
  { value: "LOW", label: "Baja" },
  { value: "MEDIUM", label: "Media" },
  { value: "HIGH", label: "Alta" },
  { value: "URGENT", label: "Urgente" },
];

const statuses: { value: TaskStatus; label: string }[] = [
  { value: "PENDING", label: "Pendiente" },
  { value: "IN_PROGRESS", label: "En Ejecución" },
  { value: "IN_REVIEW", label: "En Prueba" },
  { value: "COMPLETED", label: "Completada" },
];

export function TaskDialog({
  open,
  onOpenChange,
  task,
  projectId,
  defaultStatus,
  onSuccess,
}: TaskDialogProps) {
  const { projects } = useTaskStore();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    projectId: projectId || "",
    status: defaultStatus || "PENDING",
    progress: 0,
    prompt: "",
    priority: "MEDIUM" as Priority,
    dueDate: "",
    assigneeAgent: "",
    isRecurring: false,
    cronExpression: "",
    cronTimezone: "America/Santiago",
    cronDescription: "",
  });

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title,
        description: task.description || "",
        projectId: task.projectId,
        status: task.status,
        progress: task.progress,
        prompt: task.prompt || "",
        priority: task.priority,
        dueDate: task.dueDate ? task.dueDate.split("T")[0] : "",
        assigneeAgent: task.assigneeAgent || "",
        isRecurring: (task as any).isRecurring || false,
        cronExpression: (task as any).cronExpression || "",
        cronTimezone: (task as any).cronTimezone || "America/Santiago",
        cronDescription: (task as any).cronDescription || "",
      });
    } else {
      setFormData({
        title: "",
        description: "",
        projectId: projectId || projects[0]?.id || "",
        status: defaultStatus || "PENDING",
        progress: 0,
        prompt: "",
        priority: "MEDIUM",
        dueDate: "",
        assigneeAgent: "",
        isRecurring: false,
        cronExpression: "",
        cronTimezone: "America/Santiago",
        cronDescription: "",
      });
    }
  }, [task, projectId, defaultStatus, projects, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.projectId) {
      toast.error("El título y proyecto son requeridos");
      return;
    }

    setIsLoading(true);

    try {
      const url = task ? `/api/tasks` : `/api/tasks`;
      const method = task ? "PUT" : "POST";

      const body: Record<string, unknown> = {
        ...formData,
        ...(task && { id: task.id }),
        dueDate: formData.dueDate || undefined,
      };

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (data.success) {
        toast.success(task ? "Tarea actualizada" : "Tarea creada");
        onSuccess();
      } else {
        toast.error(data.error || "Error al guardar tarea");
      }
    } catch (error) {
      toast.error("Error al guardar tarea");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!task) return;

    setIsLoading(true);

    try {
      const response = await fetch(`/api/tasks?id=${task.id}`, {
        method: "DELETE",
      });

      const data = await response.json();

      if (data.success) {
        toast.success("Tarea eliminada");
        onSuccess();
      } else {
        toast.error(data.error || "Error al eliminar tarea");
      }
    } catch (error) {
      toast.error("Error al eliminar tarea");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{task ? "Editar Tarea" : "Nueva Tarea"}</DialogTitle>
            <DialogDescription>
              {task
                ? "Edita los detalles de la tarea"
                : "Crea una nueva tarea en el proyecto"}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Título</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                placeholder="Título de la tarea"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descripción</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Descripción de la tarea"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Proyecto</Label>
                <Select
                  value={formData.projectId}
                  onValueChange={(value) =>
                    setFormData({ ...formData, projectId: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar proyecto" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Estado</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) =>
                    setFormData({ ...formData, status: value as TaskStatus })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statuses.map((status) => (
                      <SelectItem key={status.value} value={status.value}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prioridad</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(value) =>
                    setFormData({ ...formData, priority: value as Priority })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {priorities.map((priority) => (
                      <SelectItem key={priority.value} value={priority.value}>
                        {priority.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="dueDate">Fecha límite</Label>
                <Input
                  id="dueDate"
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) =>
                    setFormData({ ...formData, dueDate: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="progress">Progreso: {formData.progress}%</Label>
              <Input
                id="progress"
                type="range"
                min="0"
                max="100"
                value={formData.progress}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    progress: parseInt(e.target.value),
                  })
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="prompt">Prompt (para agente IA)</Label>
              <Textarea
                id="prompt"
                value={formData.prompt}
                onChange={(e) =>
                  setFormData({ ...formData, prompt: e.target.value })
                }
                placeholder="Instrucciones para el agente IA..."
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="assigneeAgent">Agente IA</Label>
              <Input
                id="assigneeAgent"
                value={formData.assigneeAgent}
                onChange={(e) =>
                  setFormData({ ...formData, assigneeAgent: e.target.value })
                }
                placeholder="Ej: claude, gpt-4"
              />
            </div>

            {/* Recurrence Section */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  id="isRecurring"
                  checked={formData.isRecurring}
                  onChange={(e) =>
                    setFormData({ ...formData, isRecurring: e.target.checked })
                  }
                  className="w-4 h-4"
                />
                <Label htmlFor="isRecurring" className="font-medium">
                  Tarea Recurrente (Cron)
                </Label>
              </div>

              {formData.isRecurring && (
                <div className="space-y-3 pl-6">
                  <div className="space-y-2">
                    <Label htmlFor="cronExpression">Expresión Cron</Label>
                    <Input
                      id="cronExpression"
                      value={formData.cronExpression}
                      onChange={(e) =>
                        setFormData({ ...formData, cronExpression: e.target.value })
                      }
                      placeholder="0 * * * * (cada hora)"
                    />
                    <p className="text-xs text-muted-foreground">
                      Formato: minuto hora día-mes mes día-semana
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="cronTimezone">Zona Horaria</Label>
                      <Select
                        value={formData.cronTimezone}
                        onValueChange={(value) =>
                          setFormData({ ...formData, cronTimezone: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="America/Santiago">Santiago (UTC-3)</SelectItem>
                          <SelectItem value="America/New_York">New York (UTC-5)</SelectItem>
                          <SelectItem value="America/Los_Angeles">Los Angeles (UTC-8)</SelectItem>
                          <SelectItem value="Europe/Madrid">Madrid (UTC+1)</SelectItem>
                          <SelectItem value="Europe/London">London (UTC+0)</SelectItem>
                          <SelectItem value="UTC">UTC</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="cronDescription">Descripción</Label>
                    <Input
                      id="cronDescription"
                      value={formData.cronDescription}
                      onChange={(e) =>
                        setFormData({ ...formData, cronDescription: e.target.value })
                      }
                      placeholder="Ej: Todos los días a las 9am"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="justify-between">
            {task ? (
              <Button
                type="button"
                variant="destructive"
                onClick={handleDelete}
                disabled={isLoading}
              >
                Eliminar
              </Button>
            ) : (
              <div />
            )}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Guardando..." : "Guardar"}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
